const escapeHtml = (value = '') =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const SAFE_LINK_RE = /^(https?:|mailto:)/i;

const formatInline = (value = '') => {
  let text = escapeHtml(value);
  text = text.replace(/`([^`]+)`/g, '<code>$1</code>');
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/__(.+?)__/g, '<strong>$1</strong>');
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');
  text = text.replace(/_(.+?)_/g, '<em>$1</em>');
  text = text.replace(/\[(.+?)\]\((.+?)\)/g, (match, label, url) =>
    SAFE_LINK_RE.test(url) ? `<a href="${url}" target="_blank" rel="noreferrer">${label}</a>` : match,
  );
  return text;
};

const splitTableRow = (row) => {
  const cells = row.split('|').map((cell) => cell.trim());
  if (cells.length && cells[0] === '') {
    cells.shift();
  }
  if (cells.length && cells[cells.length - 1] === '') {
    cells.pop();
  }
  return cells;
};

const isTableDividerLine = (line) => {
  if (!line) return false;
  const cells = line
    .split('|')
    .map((cell) => cell.trim())
    .filter((cell) => cell.length > 0);
  if (!cells.length) return false;
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

const renderTableBlock = (headerCells, bodyRows) => {
  if (!headerCells.length) return '';
  const columnCount = Math.max(
    headerCells.length,
    bodyRows.reduce((max, row) => Math.max(max, row.length), 0),
    1,
  );
  const normalizedHeader = [...headerCells];
  while (normalizedHeader.length < columnCount) {
    normalizedHeader.push('');
  }
  const normalizedRows = bodyRows.map((row) => {
    const normalized = [...row];
    while (normalized.length < columnCount) {
      normalized.push('');
    }
    return normalized;
  });

  const headerHtml = normalizedHeader
    .map(
      (cell) =>
        `<th class="px-3 py-2 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">${formatInline(
          cell,
        )}</th>`,
    )
    .join('');

  const rowsHtml = normalizedRows
    .map(
      (row, rowIndex) =>
        `<tr class="${
          rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
        } border-b border-gray-200">${row
          .map(
            (cell) =>
              `<td class="px-3 py-2 align-top text-sm text-gray-800">${formatInline(cell)}</td>`,
          )
          .join('')}</tr>`,
    )
    .join('');

  return `
    <div class="mt-4 overflow-x-auto">
      <table class="w-full min-w-full border border-gray-200 divide-y divide-gray-200 text-sm text-gray-800">
        <thead class="bg-gray-50">
          <tr>${headerHtml}</tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>
    </div>
  `;
};

export const markdownToHtml = (markdown = '') => {
  if (!markdown) return '';

  const lines = markdown.split(/\r?\n/);
  const htmlParts = [];
  const paragraphBuffer = [];
  let openListType = '';

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    htmlParts.push(`<p class="text-sm leading-relaxed text-gray-800">${paragraphBuffer.join('<br/>')}</p>`);
    paragraphBuffer.length = 0;
  };

  const closeList = () => {
    if (!openListType) return;
    htmlParts.push(`</${openListType}>`);
    openListType = '';
  };

  for (let i = 0; i < lines.length; i += 1) {
    const rawLine = lines[i];
    const line = rawLine.replace(/\t/g, '    ');
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      closeList();
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      closeList();
      const level = Math.min(6, headingMatch[1].length);
      htmlParts.push(
        `<h${level} class="mt-4 text-gray-900 font-semibold">${
          formatInline(headingMatch[2])
        }</h${level}>`,
      );
      continue;
    }

    const blockquoteMatch = trimmed.match(/^>\s+(.*)$/);
    if (blockquoteMatch) {
      flushParagraph();
      closeList();
      htmlParts.push(
        `<blockquote class="border-l-4 border-gray-300 pl-4 text-gray-600 italic mb-3">${formatInline(
          blockquoteMatch[1],
        )}</blockquote>`,
      );
      continue;
    }

    const unorderedMatch = trimmed.match(/^[-*+]\s+(.*)$/);
    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (unorderedMatch || orderedMatch) {
      flushParagraph();
      const listType = orderedMatch ? 'ol' : 'ul';
      if (openListType && openListType !== listType) {
        closeList();
      }
      if (!openListType) {
        openListType = listType;
        const listClass = listType === 'ol' ? 'list-decimal' : 'list-disc';
        htmlParts.push(`<${listType} class="${listClass} pl-5 space-y-1 text-sm text-gray-800">`);
      }
      htmlParts.push(`<li>${formatInline((orderedMatch || unorderedMatch)[1])}</li>`);
      continue;
    }

    if (i + 1 < lines.length) {
      const nextLine = lines[i + 1].trim();
      if (trimmed.includes('|') && isTableDividerLine(nextLine)) {
        flushParagraph();
        closeList();
        const headerCells = splitTableRow(trimmed);
        i += 1;
        const bodyRows = [];
        while (i + 1 < lines.length) {
          const peek = lines[i + 1].trim();
          if (!peek || !peek.includes('|')) {
            break;
          }
          i += 1;
          bodyRows.push(splitTableRow(peek));
        }
        htmlParts.push(renderTableBlock(headerCells, bodyRows));
        continue;
      }
    }

    paragraphBuffer.push(formatInline(trimmed));
  }

  if (paragraphBuffer.length) {
    htmlParts.push(`<p class="text-sm leading-relaxed text-gray-800">${paragraphBuffer.join('<br/>')}</p>`);
  }
  closeList();

  return htmlParts.join('');
};

const escapeRtf = (value = '') =>
  value
    .replace(/\\/g, '\\\\')
    .replace(/{/g, '\\{')
    .replace(/}/g, '\\}')
    .replace(/\r?\n/g, '\\par ');

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;
const bodyFont = 24;
const headingSizes = { 1: 48, 2: 44, 3: 40, 4: 36, 5: 32, 6: 28 };

const simpleMarkdownToRtf = (markdown = '') => {
  if (!markdown) {
    return '{\\rtf1\\ansi\\deff0}';
  }
  const lines = markdown.split(/\r?\n/);
  const bodyLines = [];

  lines.forEach((rawLine) => {
    const trimmed = rawLine.trim();
    if (!trimmed) {
      bodyLines.push('\\par');
      return;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = Math.min(6, headingMatch[1].length);
      const size = headingSizes[level] || 32;
      bodyLines.push(
        `\\pard\\b\\fs${size} ${escapeRtf(headingMatch[2])}\\b0\\fs${bodyFont}\\par`,
      );
      return;
    }

    const listMatch = trimmed.match(/^[-*+]\s+(.*)$/);
    if (listMatch) {
      bodyLines.push(`\\pard\\bullet ${escapeRtf(listMatch[1])}\\par`);
      return;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) {
      bodyLines.push(`\\pard ${escapeRtf(orderedMatch[1])}\\par`);
      return;
    }

    bodyLines.push(`\\pard ${escapeRtf(trimmed)}\\par`);
  });

  return `{\n\\rtf1\\ansi\\deff0\n${bodyLines.join('\n')}\n}`;
};

const renderHtmlToRtf = (html) => {
  if (!html || typeof DOMParser === 'undefined') {
    return '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body.firstElementChild;
  if (!container) {
    return '';
  }

  const builder = [];
  const listStack = [];
  const paragraphPrefix = `\\pard\\sa120\\sl280\\fs${bodyFont}`;
  const tableRowPrefix = '\\trowd\\trgaph108';
  const tableCellPrefix = `${paragraphPrefix}\\intbl `;

  function renderChildren(nodeList) {
    if (!nodeList) {
      return;
    }
    Array.from(nodeList).forEach((child) => renderNode(child));
  }

  const collectRows = (element) => {
    const rows = [];
    Array.from(element.childNodes).forEach((child) => {
      if (child.nodeType !== ELEMENT_NODE) return;
      const tag = child.tagName.toLowerCase();
      if (tag === 'tr') {
        rows.push(child);
      } else if (['thead', 'tbody', 'tfoot'].includes(tag)) {
        rows.push(...collectRows(child));
      }
    });
    return rows;
  };

  const renderTableRows = (rows) => {
    rows.forEach((row) => {
      const cells = Array.from(row.childNodes).filter(
        (node) =>
          node.nodeType === ELEMENT_NODE &&
          ['td', 'th'].includes(node.tagName.toLowerCase()),
      );
      if (!cells.length) {
        return;
      }
      builder.push(`${tableRowPrefix} `);
      cells.forEach((cell) => {
        const tag = cell.tagName.toLowerCase();
        const isHeader = tag === 'th';
        builder.push(`${tableCellPrefix}`);
        if (isHeader) {
          builder.push('\\b ');
        }
        renderChildren(cell.childNodes);
        if (isHeader) {
          builder.push('\\b0 ');
        }
        builder.push('\\cell ');
      });
      builder.push('\\row ');
    });
  };

  function renderNode(node) {
    if (!node) {
      return;
    }
    if (node.nodeType === TEXT_NODE) {
      builder.push(escapeRtf(node.nodeValue || ''));
      return;
    }
    if (node.nodeType !== ELEMENT_NODE) {
      return;
    }

    const tag = node.tagName.toLowerCase();
    switch (tag) {
      case 'p':
        builder.push(`${paragraphPrefix} `);
        renderChildren(node.childNodes);
        builder.push('\\par ');
        break;
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6': {
        const level = Number(tag.replace('h', ''));
        const size = headingSizes[level] || headingSizes[6];
        builder.push(`${paragraphPrefix} `);
        builder.push(`\\b\\fs${size} `);
        renderChildren(node.childNodes);
        builder.push(`\\b0\\fs${bodyFont} \\par `);
        break;
      }
      case 'blockquote':
        builder.push(`${paragraphPrefix}\\li720 `);
        renderChildren(node.childNodes);
        builder.push('\\par\\li0 ');
        break;
      case 'strong':
      case 'b':
        builder.push('\\b ');
        renderChildren(node.childNodes);
        builder.push('\\b0 ');
        break;
      case 'em':
      case 'i':
        builder.push('\\i ');
        renderChildren(node.childNodes);
        builder.push('\\i0 ');
        break;
      case 'ul':
      case 'ol':
        listStack.push({ type: tag, index: 1 });
        renderChildren(node.childNodes);
        listStack.pop();
        break;
      case 'li': {
        const currentList = listStack[listStack.length - 1];
        const indentLevel = Math.max(listStack.length, 1);
        const indent = 720 * indentLevel;
        builder.push(`${paragraphPrefix}\\li${indent} `);
        if (currentList?.type === 'ul') {
          builder.push('\\bullet ');
        } else if (currentList?.type === 'ol') {
          builder.push(`${currentList.index}. `);
          currentList.index += 1;
        }
        renderChildren(node.childNodes);
        builder.push('\\par ');
        break;
      }
      case 'br':
        builder.push('\\line ');
        break;
      case 'table': {
        builder.push('\\par ');
        const rows = collectRows(node);
        renderTableRows(rows);
        builder.push('\\par ');
        break;
      }
      case 'tbody':
      case 'thead':
      case 'tfoot':
      case 'tr':
      case 'td':
      case 'th':
        // Already handled in table rendering
        break;
      default:
        renderChildren(node.childNodes);
    }
  }

  renderChildren(container.childNodes);
  return builder.join('').trim();
};

export const markdownToRtf = (markdown = '') => {
  if (!markdown) {
    return '{\\rtf1\\ansi\\deff0}';
  }

  const html = markdownToHtml(markdown);
  if (typeof DOMParser === 'undefined') {
    return simpleMarkdownToRtf(markdown);
  }

  try {
    const body = renderHtmlToRtf(html);
    if (!body) {
      return simpleMarkdownToRtf(markdown);
    }
    return `{\n\\rtf1\\ansi\\deff0\n${body}\n}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to render RTF from HTML', error);
    return simpleMarkdownToRtf(markdown);
  }
};
